import { useState } from "react";
import type { CompanyInfo, RedSocial } from "@/types";

interface ConfiguracionFormProps {
  initial: CompanyInfo;
  saving: boolean;
  onSave: (info: CompanyInfo) => Promise<void>;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-700";

const iconOptions = [
  "ri-facebook-fill",
  "ri-instagram-line",
  "ri-linkedin-fill",
  "ri-youtube-fill",
  "ri-tiktok-fill",
  "ri-twitter-x-fill",
  "ri-whatsapp-fill",
];

function Card({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="text-base font-heading font-semibold text-foreground-950">{title}</h2>
      {description && <p className="mt-1 text-sm text-foreground-600">{description}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

export default function ConfiguracionForm({ initial, saving, onSave }: ConfiguracionFormProps) {
  const [info, setInfo] = useState<CompanyInfo>(initial);
  const [error, setError] = useState("");

  const set = (name: keyof CompanyInfo, value: unknown) =>
    setInfo((prev) => ({ ...prev, [name]: value }));

  const updateRed = (index: number, name: keyof RedSocial, value: string) =>
    setInfo((prev) => ({
      ...prev,
      redes: prev.redes.map((red, i) => (i === index ? { ...red, [name]: value } : red)),
    }));

  const addRed = () =>
    setInfo((prev) => ({ ...prev, redes: [...prev.redes, { nombre: "Nueva red", icono: "ri-links-line", url: "" }] }));

  const removeRed = (index: number) =>
    setInfo((prev) => ({ ...prev, redes: prev.redes.filter((_, i) => i !== index) }));

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!info.nombre.trim() || !info.email.trim() || !info.telefono.trim()) {
      setError("Completa el nombre de la empresa, el teléfono y el correo.");
      return;
    }
    const telefonoDigits = info.telefono.replace(/[^\d+]/g, "");
    const whatsappDigits = info.whatsapp.replace(/\D/g, "") || telefonoDigits.replace(/\D/g, "");
    await onSave({
      ...info,
      telefonoLink: `tel:${telefonoDigits}`,
      emailLink: `mailto:${info.email.trim()}`,
      whatsappLink: whatsappDigits ? `https://wa.me/${whatsappDigits}` : "",
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <Card title="Datos de la empresa" description="Aparecen en el encabezado, el pie de página y las secciones principales.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Nombre de la empresa</label>
            <input value={info.nombre} onChange={(e) => set("nombre", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Lema</label>
            <input value={info.lema} onChange={(e) => set("lema", e.target.value)} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Descripción corta</label>
            <textarea value={info.descripcionCorta} onChange={(e) => set("descripcionCorta", e.target.value)} rows={2} className={`${inputClass} resize-y`} />
          </div>
        </div>
      </Card>

      <Card title="Contacto" description="Datos que verán los clientes para comunicarse contigo.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Teléfono</label>
            <input value={info.telefono} onChange={(e) => set("telefono", e.target.value)} className={inputClass} placeholder="+51 987 654 321" />
          </div>
          <div>
            <label className={labelClass}>WhatsApp</label>
            <input value={info.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputClass} placeholder="+51 987 654 321" />
          </div>
          <div>
            <label className={labelClass}>Correo</label>
            <input type="email" value={info.email} onChange={(e) => set("email", e.target.value)} className={inputClass} placeholder="contacto@empresa.com" />
          </div>
          <div>
            <label className={labelClass}>Horario de atención</label>
            <input value={info.horario} onChange={(e) => set("horario", e.target.value)} className={inputClass} placeholder="Lunes a Sábado · 7:00 a 20:00" />
          </div>
          <div>
            <label className={labelClass}>Dirección</label>
            <input value={info.direccion} onChange={(e) => set("direccion", e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Ciudad</label>
            <input value={info.ciudad} onChange={(e) => set("ciudad", e.target.value)} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Mapa (URL de inserción de Google Maps)</label>
            <input value={info.mapaEmbed} onChange={(e) => set("mapaEmbed", e.target.value)} className={inputClass} placeholder="https://www.google.com/maps?q=...&output=embed" />
          </div>
        </div>
      </Card>

      <Card title="Redes sociales" description="Enlaces a tus perfiles. Se muestran en el pie de página.">
        <div className="space-y-3">
          {info.redes.map((red, index) => (
            <div key={index} className="grid grid-cols-1 gap-3 rounded-md border border-background-200 bg-background-100 p-3 md:grid-cols-[1fr_1.4fr_auto]">
              <input value={red.nombre} onChange={(e) => updateRed(index, "nombre", e.target.value)} placeholder="Nombre" className={inputClass} />
              <input value={red.url} onChange={(e) => updateRed(index, "url", e.target.value)} placeholder="https://…" className={inputClass} />
              <div className="flex items-center gap-2">
                <select value={red.icono} onChange={(e) => updateRed(index, "icono", e.target.value)} className={`${inputClass} w-auto`} aria-label="Icono">
                  {iconOptions.map((icon) => (
                    <option key={icon} value={icon}>{icon.replace("ri-", "").replace("-fill", "").replace("-line", "")}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeRed(index)}
                  aria-label="Quitar red"
                  className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-delete-bin-6-line text-base leading-none" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addRed}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            <i className="ri-add-line text-base leading-none" aria-hidden="true"></i>
            Agregar red social
          </button>
        </div>
      </Card>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-foreground-950"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base leading-none" aria-hidden="true"></i>
              Guardando…
            </>
          ) : (
            <>
              <i className="ri-save-3-line text-base leading-none" aria-hidden="true"></i>
              Guardar configuración
            </>
          )}
        </button>
      </div>
    </form>
  );
}